import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { PriceTier, RecommendedRoutine, RecommendedStep, LegacyBudgetTier, BudgetRoutines } from '@/types/product'
import type { RoutineType } from '@/types/product'
import { mockRoutines, calculateRoutineCost } from '@/data/mockProducts'
import { buildRoutine, type UserProfile, type AnalysisScores } from '@/services/recommendationEngine'
import { getAllProducts } from '@/services/productService'
import type { WeatherInsight } from '@/services/weatherService'
import { useAnalysisStore } from '@/store/analysisStore'
import { useSurveyStore } from '@/store/surveyStore'
import { useFullSurveyStore } from '@/store/fullSurveyStore'
import type { FullSurveyData, SkinCondition } from '@/types/fullSurvey'
import type { ShortSurveyData } from '@/types/survey'

// Default weather for fallback
const DEFAULT_WEATHER: WeatherInsight = {
  location: 'Unknown',
  tempRange: { min: 60, max: 80 },
  avgHumidity: 50,
  uvIndex: 5,
  season: 'summer',
  skinImplications: ['Normal humidity levels'],
  textureRecommendation: 'medium',
}

// Default full survey data for fallback
export const DEFAULT_FULL_SURVEY: FullSurveyData = {
  ethnicity: null,
  zipCode: null,
  routineFrequency: null,
  skinColor: null,
  currentConditions: [],
  includeIngredients: [],
  avoidIngredients: [],
  texturePreference: 'no-preference',
  routineLength: 'moderate',
  pregnancyStatus: 'not-applicable',
}

/**
 * Merge short survey and full survey data.
 * Combines skin conditions from both surveys (deduplicated).
 * Uses fullSurvey preferences when available, falls back to defaults.
 */
export function mergeSurveyData(
  shortSurvey: { skinConditions?: SkinCondition[] },
  fullSurvey: Partial<FullSurveyData>
): FullSurveyData {
  const shortConditions = shortSurvey.skinConditions ?? []
  const fullConditions = fullSurvey.currentConditions ?? []
  const mergedConditions = [...new Set([...shortConditions, ...fullConditions])] as SkinCondition[]

  return {
    ...DEFAULT_FULL_SURVEY,
    ...fullSurvey,
    currentConditions: mergedConditions,
  }
}

interface ReportState {
  // Selected budget tier for display (now using PriceTier)
  selectedBudget: PriceTier

  // New: Recommended routine from the engine
  recommendedRoutine: RecommendedRoutine | null
  isLoadingRoutine: boolean
  routineError: string | null

  // Legacy: Routines data for backwards compatibility
  routines: BudgetRoutines | null

  // PDF state
  isPdfGenerating: boolean
  pdfUrl: string | null

  // Actions
  setSelectedBudget: (tier: PriceTier) => void
  setRoutines: (routines: BudgetRoutines) => void
  loadMockRoutines: () => void

  // New: Load real recommendations
  loadRealRoutines: (routineType?: RoutineType) => Promise<void>

  // PDF actions
  generatePdf: () => Promise<void>
  setPdfUrl: (url: string | null) => void

  // Computed
  getCurrentRoutineCost: () => { am: number; pm: number; total: number }

  // New: Get products for current tier
  getStepsForTier: (time: 'am' | 'pm') => RecommendedStep[]

  // Reset
  resetReport: () => void
}

export const useReportStore = create<ReportState>()(
  persist(
    (set, get) => ({
      selectedBudget: 'mainstream',
      recommendedRoutine: null,
      isLoadingRoutine: false,
      routineError: null,
      routines: null,
      isPdfGenerating: false,
      pdfUrl: null,

      setSelectedBudget: (tier) =>
        set({ selectedBudget: tier }),

      setRoutines: (routines) =>
        set({ routines }),

      loadMockRoutines: () =>
        set({ routines: mockRoutines }),

      /**
       * Load real product recommendations based on user's analysis and preferences
       */
      loadRealRoutines: async (routineType: RoutineType = 'essential') => {
        set({ isLoadingRoutine: true, routineError: null })

        try {
          // 1. Fetch products from DynamoDB
          console.log('[ReportStore] Fetching products...')
          const products = await getAllProducts()
          console.log('[ReportStore] Got', products.length, 'products')

          if (products.length === 0) {
            throw new Error('No products available')
          }

          // 2. Get user data from stores
          const analysisResults = useAnalysisStore.getState().analysisResults
          const shortSurvey = useSurveyStore.getState().shortSurvey
          const fullSurvey = useFullSurveyStore.getState().fullSurvey

          // 3. Convert analysis results to AnalysisScores format
          const analysisScores: AnalysisScores = {}
          if (analysisResults?.metrics) {
            // Map frontend metrics to HD API field names
            if (analysisResults.metrics.wrinkles !== undefined) {
              analysisScores.hd_wrinkle = analysisResults.metrics.wrinkles
            }
            if (analysisResults.metrics.pores !== undefined) {
              analysisScores.hd_pore = analysisResults.metrics.pores
            }
            if (analysisResults.metrics.texture !== undefined) {
              analysisScores.hd_texture = analysisResults.metrics.texture
            }
            if (analysisResults.metrics.acne !== undefined) {
              analysisScores.hd_acne = analysisResults.metrics.acne
            }
            if (analysisResults.metrics.oiliness !== undefined) {
              analysisScores.hd_oiliness = analysisResults.metrics.oiliness
            }
            if (analysisResults.metrics.brightness !== undefined) {
              analysisScores.hd_radiance = 100 - analysisResults.metrics.brightness // Invert
            }
            if (analysisResults.metrics.redness !== undefined) {
              analysisScores.hd_redness = analysisResults.metrics.redness
            }
            if (analysisResults.metrics.pigmentation !== undefined) {
              analysisScores.hd_age_spot = analysisResults.metrics.pigmentation
            }
          }

          // 4. Get weather data
          // TODO: Use ZIP code from full survey once implemented
          // For now, use default weather
          const weather = DEFAULT_WEATHER

          // 5. Build user profile - merge both surveys
          const effectiveFullSurvey = mergeSurveyData(shortSurvey, fullSurvey)

          const profile: UserProfile = {
            analysisScores,
            survey: shortSurvey as ShortSurveyData,
            fullSurvey: effectiveFullSurvey,
            weather,
            routineType,
          }

          console.log('[ReportStore] Building routine with profile:', {
            concerns: Object.entries(analysisScores).filter(([, v]) => v && v >= 40).length,
            skinType: shortSurvey.skinType,
            skinConditions: effectiveFullSurvey.currentConditions,
            routineType,
          })

          // 6. Build the routine
          const routine = buildRoutine(products, profile)

          console.log('[ReportStore] Routine built:', {
            amSteps: routine.am.length,
            pmSteps: routine.pm.length,
            totalPrice: routine.totalPrice,
          })

          set({
            recommendedRoutine: routine,
            isLoadingRoutine: false,
            routineError: null,
          })
        } catch (error) {
          console.error('[ReportStore] Failed to load routines:', error)
          set({
            isLoadingRoutine: false,
            routineError: error instanceof Error ? error.message : 'Failed to load recommendations',
          })

          // Fall back to mock routines
          set({ routines: mockRoutines })
        }
      },

      generatePdf: async () => {
        set({ isPdfGenerating: true })

        // Simulate PDF generation
        await new Promise((resolve) => setTimeout(resolve, 2000))

        // Mock PDF URL
        const mockPdfUrl = '/reports/mock-report.pdf'

        set({
          isPdfGenerating: false,
          pdfUrl: mockPdfUrl,
        })
      },

      setPdfUrl: (url) =>
        set({ pdfUrl: url }),

      getCurrentRoutineCost: () => {
        const { recommendedRoutine, selectedBudget, routines } = get()

        // Use new recommended routine if available
        if (recommendedRoutine) {
          const tier = selectedBudget as PriceTier

          // Calculate AM/PM costs
          let amCost = 0
          let pmCost = 0
          const seenIds = new Set<string>()

          for (const step of recommendedRoutine.am) {
            const product = step.products[tier]
            if (product && !seenIds.has(product.id)) {
              amCost += product.price
              seenIds.add(product.id)
            }
          }

          for (const step of recommendedRoutine.pm) {
            const product = step.products[tier]
            if (product && !seenIds.has(product.id)) {
              pmCost += product.price
              seenIds.add(product.id)
            }
          }

          return { am: amCost, pm: pmCost, total: amCost + pmCost }
        }

        // Fallback to legacy routines
        if (!routines) {
          return { am: 0, pm: 0, total: 0 }
        }

        // Map new tier names to legacy
        const legacyTierMap: Record<PriceTier, LegacyBudgetTier> = {
          affordable: 'essential',
          mainstream: 'standard',
          premium: 'premium',
        }
        const legacyTier = legacyTierMap[selectedBudget as PriceTier] || 'standard'

        const currentRoutine = routines[legacyTier]
        if (!currentRoutine) {
          return { am: 0, pm: 0, total: 0 }
        }

        const amCost = calculateRoutineCost(currentRoutine.am)
        const pmCost = calculateRoutineCost(currentRoutine.pm)

        // PM products that aren't in AM
        const amProductIds = new Set(currentRoutine.am.map((s) => s.product.id))
        const uniquePmCost = currentRoutine.pm
          .filter((s) => !amProductIds.has(s.product.id))
          .reduce((total, s) => total + s.product.price, 0)

        return {
          am: amCost,
          pm: pmCost,
          total: amCost + uniquePmCost,
        }
      },

      getStepsForTier: (time: 'am' | 'pm') => {
        const { recommendedRoutine } = get()
        if (!recommendedRoutine) return []
        return time === 'am' ? recommendedRoutine.am : recommendedRoutine.pm
      },

      resetReport: () =>
        set({
          selectedBudget: 'mainstream',
          recommendedRoutine: null,
          isLoadingRoutine: false,
          routineError: null,
          routines: null,
          isPdfGenerating: false,
          pdfUrl: null,
        }),
    }),
    {
      name: 'k-skin-report',
      partialize: (state) => ({
        selectedBudget: state.selectedBudget,
        pdfUrl: state.pdfUrl,
      }),
    }
  )
)
